import type { MessageKey } from "./catalog.th.ts";

/**
 * English catalog (I18N-002 — ships complete alongside `th`).
 *
 * Tone: warm, easy-going nightlife English for tourists/expats — natural
 * phrasing, not literal word-for-word translation. The `Record<MessageKey,
 * string>` annotation makes a missing OR extra key a compile error, so the
 * two catalogs can never drift apart silently.
 *
 * `mode.*` follows the LOCKED Tonight Mode vocabulary (I18N-006) — the
 * English equivalents are fixed here; do not paraphrase per-screen.
 */
export const en: Record<MessageKey, string> = {
  // Brand — the Thai product name IS the brand; identical in every locale.
  "brand.appName": "วันนี้เบาๆนะ",

  // Locale switcher labels — each language names itself, never translated.
  "locale.th": "ไทย",
  "locale.en": "EN",
  "settings.language": "Language",

  // Common / shared
  "common.you": "You",
  "common.back": "Back",
  "common.backHome": "Back to home",
  "common.notYet": "Not yet",
  "common.table": "Table {code}",
  "common.toTable": "To table {table}",
  "common.saving": "Saving...",
  "common.itemsCount": "{count} items",
  "common.cantSendYet": "Can't send just yet",
  "common.block": "Block",
  "common.report": "Report",
  "common.profileTonight": "Tonight's profile",

  // Root LIFF gate
  "root.connecting": "Connecting…",
  "root.lineConnectFailed": "Couldn't connect to LINE",
  "root.openInLineTitle": "Open in the LINE app",
  "root.openInLineDesc": "Please open this link in the LINE app to start using Just One Drink",
  "root.loginWithLine": "Log in with LINE",

  // Connectivity banner (FR-OFF-002)
  "connectivity.offline":
    "You're offline · data may be out of date. Pending sends go out automatically once you're back online",
  "connectivity.degraded":
    "The network is slow right now · data may not be the latest. Retrying automatically",

  // Bottom navigation
  "nav.home": "Home",
  "nav.order": "Order",
  "nav.around": "Around",
  "nav.inbox": "Inbox",
  "nav.me": "Me",

  // Welcome
  "welcome.tonightAt": "Tonight at {venue} · {session}",
  "welcome.start": "Start easy",
  "welcome.orderOnly": "Just order drinks",

  // Age gate + consent
  "ageGate.kicker": "Before tonight begins",
  "ageGate.title": "Drink easy · You're in control",
  "ageGate.confirmAge": "I confirm I am 20 or older",
  "ageGate.confirmConsent": "I accept the privacy and consent terms ({version})",
  "ageGate.blockedNote":
    "Thanks for stopping by · this service is for guests aged 20 and over only",
  "ageGate.confirm": "Confirm and enter",
  "ageGate.under20": "I'm under 20",

  // LINE profile onboarding
  "lineProfile.changePhotoAria": "Change profile photo",
  "lineProfile.changePhoto": "Change photo",
  "lineProfile.useLinePhoto": "Use LINE photo",
  "lineProfile.useGlyph": "Or use an icon instead of a photo",
  "lineProfile.namePlaceholder": "The name you'd like others to see",
  "lineProfile.privacyNote": "Only you can see this photo and name until you choose to go visible",
  "lineProfile.useProfile": "Use this profile",

  // Join table / check-in
  "joinTable.kicker": "Check in",
  "joinTable.orderOnlyTitle": "Order without a table",
  "joinTable.confirmTitle": "This is your table",
  "joinTable.tokenInvalid":
    "This QR token has expired or doesn't match tonight's session · pick a table below or rescan at your table",
  "joinTable.branch": "Branch",
  "joinTable.tableKicker": "Table",
  "joinTable.friendsCheckedIn": "{count} friends already checked in",
  "joinTable.pickHint": "Pick your table, or scan the QR at your table again",
  "joinTable.noTableChip": "No table",
  "joinTable.enterOrderOnly": "Enter order-only",
  "joinTable.enterTable": "Join this table",
  "joinTable.enterConfirm": "Yes, join this table",
  "joinTable.notMyTable": "Not my table?",

  // Tonight-mode picker screen
  "tonightMode.kicker": "Tonight's mode",
  "tonightMode.title": "How open do you feel tonight?",
  "tonightMode.previewHiddenKicker": "In the room right now",
  "tonightMode.previewVisibleKicker": "Other tables will see you like this",
  "tonightMode.hidden": "Hidden",
  "tonightMode.hiddenSub": "No one can see your table",
  "tonightMode.tableAndMode": "Table {table} · {mode}",
  "tonightMode.enter": "Enter the venue",

  // Tonight Mode vocabulary — LOCKED (I18N-006)
  "mode.private.title": "Private",
  "mode.private.desc": "Stay unseen · just here to order",
  "mode.private.preview": "Hidden · no one can see your table",
  "mode.soft_open.title": "Soft open",
  "mode.soft_open.desc": "Name/photo visible, but they ask before chatting",
  "mode.soft_open.preview": "Your name is visible · people must ask before saying hi",
  "mode.with_friends.title": "With friends",
  "mode.with_friends.desc": "Open as a group, here together",
  "mode.with_friends.preview": "Open as a group · here with friends",
  "mode.lets_cheers.title": "Let's cheers",
  "mode.lets_cheers.desc": "Open to toasts · trading drinks welcome",
  "mode.lets_cheers.preview": "Open to cheers & trading drinks",

  // Home tab
  "home.loadingVenue": "Loading venue...",
  "home.sendDrinkTitle": "Send a Just One Drink",
  "home.sendDrinkSub": "A low-key gift to another table · they accept before it's served",
  "home.tileMenu": "Menu",
  "home.tileFloor": "Floor plan",
  "home.tileFloorSub": "See tables around you",
  "home.tileGames": "Play something",
  "home.tileScreen": "Big screen",
  "home.tileScreenSub": "Post on the venue screen",
  "home.nowKicker": "Right now",
  "home.ourTable": "Our table",
  "home.orderRow": "Your table's order",
  "home.orderInProgress": "In progress",
  "home.requestsPending": "{count} waiting for you",
  "home.requestsNone": "Nothing pending",
  "home.reorder": "Reorder from the last round",

  // Menu tab
  "menu.kicker": "Tonight's menu",
  "menu.title": "Bar & Kitchen",
  "menu.loading": "Loading menu from the API...",
  "menu.loadError": "Couldn't load the menu from the API — no mock fallback in backend mode",
  "menu.alcoholBadge": "Alcohol 18+",
  "menu.soldOutBadge": "Sold out",
  "menu.soldOut": "Out",
  "menu.add": "Add",
  "menu.addedToast": "Added {item} to your cart",
  "menu.viewCart": "View cart",

  // Cart
  "cart.backToMenu": "Back to menu",
  "cart.title": "Our table's cart",
  "cart.emptyTitle": "Nothing in the cart yet",
  "cart.emptyDesc": "Pick a drink or a bite from the menu",
  "cart.viewMenu": "Browse the menu",
  "cart.qtyAria": "Quantity of {item}",
  "cart.total": "Total",
  "cart.payAtVenue": "Pay at the venue · settling later is fine",
  "cart.submit": "Send the order to our table",

  // Live order status
  "order.step.pending": "Order sent",
  "order.step.accepted": "Venue accepted",
  "order.step.preparing": "Being prepared",
  "order.step.ready": "Ready to serve",
  "order.step.served": "Served",
  "order.cancelledTitle": "Order cancelled",
  "order.cancelledBody":
    "This mock keeps the reason so the backend can tie it to audit/refund later",
  "order.rejectedTitle": "The venue declined this order",
  "order.rejectedBody": "Maybe out of stock or past kitchen hours · you won't be charged for this",
  "order.servedBody": "This order is all done",
  "order.none": "No orders yet",
  "order.goToMenu": "Go to the menu",
  "order.title": "Your order",
  "order.reason": "Reason: {reason}",
  "order.activeBackend": "The venue is on it",
  "order.activeMock": "A mock staff/status event is moving this along",
  "order.total": "Total",
  "order.statusKicker": "Status from the venue",
  "order.statusAuto": "Auto-updates every 5 seconds from Staff Ops · no need to refresh",
  "order.mockHint": "Simulates Staff Ops events before the real backend",
  "order.viewAround": "Look around while you wait",

  // Around tab
  "around.floorPlanRow": "See tonight's floor plan",
  "around.openMap": "Open map →",
  "around.youHidden": "You're hidden tonight",
  "around.youOpen": "You're open tonight · {mode}",
  "around.visibilityNote": "Other tables only see what you allow",
  "around.change": "Change",
  "around.kicker": "Around the venue",
  "around.title": "Look around",
  "around.openCount": "{count} tables open",
  "around.loading": "Loading tables from the API",
  "around.emptyTitle": "The room is still quiet",
  "around.loadError": "Couldn't load tables from the API — no mock fallback in backend mode",
  "around.emptyBody": "No tables have opened up tonight yet — check back in a bit",
  "around.acceptingGifts": "Open to receiving drinks",
  "around.openTonight": "Out tonight",
  "around.playing": "Playing",

  // Inbox (gifts + contact)
  "inbox.loadingTitle": "Loading your inbox",
  "inbox.emptyTitle": "Your inbox is empty",
  "inbox.loadError": "Couldn't load gifts from the API — try again",
  "inbox.emptyBody": "When someone sends a Just One Drink or says hi, it lands here",
  "inbox.kicker": "Your inbox",
  "inbox.title": "Requests & gifts",
  "inbox.subtitle": "Accept before it's served · decline/block anytime",
  "inbox.unknownTable": "Mystery",
  "inbox.sentToYou": "Table {table} sent you one",
  "inbox.giftAcceptNote": "If you accept, the venue serves it to your table once staff fulfil it",
  "inbox.igAttached": "with an IG request attached",
  "inbox.acceptGift": "Accept, thanks",
  "inbox.acceptGiftToast": "Accepted · waiting for staff to fulfil",
  "inbox.declineSoft": "Politely decline",
  "inbox.declinedToast": "Politely declined",
  "inbox.blockToast": "Blocked · you won't see requests from this table again",
  "inbox.reportToast": "Reported · the team will take a look",
  "inbox.greetsYou": "Table {table} says hi",
  "inbox.requestSubject": "Asking for {subject}",
  "inbox.contactNote": "They'll only see it once you accept",
  "inbox.acceptedAwaitStaff": "Accepted · waiting for staff",
  "inbox.acceptContact": "Accept request",
  "inbox.acceptContactToast": "Request accepted",
  "inbox.fulfillToast": "Staff mock fulfilled · ready to serve",
  "inbox.staffRejectToast": "Staff rejected · mock refund/void recorded",
  "inbox.finalStaffRejected": "Staff declined · mock refund/void",
  "inbox.finalDeclined": "Declined",
  "inbox.finalExpired": "Expired",

  // Contact requests screen
  "requests.kicker": "Say-hi requests",
  "requests.subtitle": "Every request waits for an accept before any contact is revealed",
  "requests.loading": "Loading requests",
  "requests.emptyTitle": "No requests yet",
  "requests.emptyBody": "Say-hi requests that need your consent will show up here",
  "requests.viewInbox": "View your inbox",
  "requests.awaitingYou": "Waiting on you",
  "requests.noIncoming": "No incoming requests",
  "requests.sentSection": "Sent by you",
  "requests.noSent": "You haven't sent any requests",
  "requests.acceptToastConsent": "Accepted · only the channels you consented to are shared",
  "requests.rejectToast": "Request declined",
  "requests.consentNote": "Contact is revealed only after you accept",
  "requests.acceptedConsent": "Accepted · only the channels you consented to are shared",
  "requests.reject": "Decline",
  "requests.presetFallback": "A gentle hello",
  "requests.asksFor": "Table {table} is asking for {subject}",
  "requests.acceptFromToast": "Accepted the request from table {table}",
  "requests.blockTableToast": "Blocked table {table}",

  // Me tab
  "me.leaveBlockedFallback": "Can't leave just yet",
  "me.deleteBackendOnly": "Data deletion is only available in backend mode",
  "me.deletedToast": "Your profile data has been deleted",
  "me.deleteFailedToast": "Couldn't delete your data — try again",
  "me.lastOrder": "Latest order: {status}",
  "me.editProfile": "Edit my profile",
  "me.adjustMode": "Adjust tonight's mode",
  "me.allowGifts": "Receive Just One Drink",
  "me.allowGiftsSub": "Let other tables send you a drink",
  "me.giftsOffToast": "No longer receiving drinks",
  "me.giftsOnToast": "Now receiving drinks",
  "me.allowContact": "Receive say-hi requests",
  "me.allowContactSub": "Let others request contact (you choose how to reply)",
  "me.contactOffToast": "Say-hi requests turned off",
  "me.contactOnToast": "Say-hi requests turned on",
  "me.safetyKicker": "Tonight's safety",
  "me.none": "None",
  "me.reports": "Issue reports",
  "me.noneYet": "None yet",
  "me.pendingConsent": "Requests awaiting consent",
  "me.sentOut": "{count} sent",
  "me.leave": "Leave venue / close this page",
  "me.deleteMyData": "Delete my data",
  "me.leaveConfirmTitle": "Leave the venue tonight?",
  "me.leaveConfirmBusy":
    "Staff are still handling an order — this mock keeps you here until it's closed",
  "me.leaveConfirmBody":
    "We'll clear your cart and outgoing requests, and switch you back to private",
  "me.leaveConfirm": "Leave venue",
  "me.deleteConfirmTitle": "Delete my profile data?",
  "me.deleteConfirmBody":
    "Your display name, bio, chosen vibes, and Instagram will be deleted immediately and can't be recovered. You can stay at the venue and keep ordering as usual",
  "me.deleteConfirm": "Delete permanently",

  // Blocked tables (FR-CUS-124)
  "blocked.kicker": "Safety",
  "blocked.title": "Blocked tables",
  "blocked.subtitle": "Blocked people can't send you drinks or say hi · unblock anytime",
  "blocked.loading": "Loading",
  "blocked.emptyTitle": "No one is blocked yet",
  "blocked.emptyBody": "Tables you block will show up here",
  "blocked.unblock": "Unblock",
  "blocked.unblockedToast": "Unblocked {name}",
  "blocked.unblockFailed": "Couldn't unblock — try again",
  "blocked.confirmTitle": "Unblock {name}?",
  "blocked.confirmTableTitle": "Unblock table {code}?",
  "blocked.confirmBody": "This table will be able to send you drinks or say hi again",
  "blocked.unblockedTableToast": "Unblocked table {code}",

  // Profile editor
  "profile.kicker": "My profile",
  "profile.saveFailed": "Couldn't save your profile — try again",
  "profile.savedToast": "Tonight's profile saved",
  "profile.galleryTitle": "Gallery",
  "profile.galleryHint": "Photos for the people you accept as tonight-friends",
  "profile.removePhotoAria": "Remove photo",
  "profile.introTitle": "A short intro",
  "profile.statusPlaceholder": "Tonight's status, e.g. here for the chill music",
  "profile.bioPlaceholder": "Tell people a little about you (optional)",
  "profile.vibeTitle": "What's your vibe tonight",
  "profile.socialTitle": "Socials",
  "profile.socialHint": "Shared only after you both accept · never shown in the feed",
  "profile.igPlaceholder": "instagram (e.g. @yourname)",
  "profile.exposureTitle": "Who sees what",
  "profile.exposureHint": "You control how much the people around can see",
  "profile.tierHidden": "While hidden",
  "profile.tierHiddenValue": "No one can see you at all",
  "profile.whenOpen": "While visible · people around can see",
  "profile.exposureBase": "Name + profile photo",
  "profile.exposureBaseSub": "The base layer — always on while visible",
  "profile.exposureFirstPhoto": "First gallery photo",
  "profile.exposureVibe": "Vibe tags",
  "profile.exposureBio": "Short intro (bio)",
  "profile.save": "Save profile",

  // Table detail
  "table.loading": "Loading table…",
  "table.notFound": "Table not found",
  "table.backToAround": "Back to look around",
  "table.peopleCount": "{count} people",
  "table.bioLine": "{label} · {count} people",
  "table.giftsClosed": "This table isn't accepting drinks right now",
  "table.contactClosed": "This table isn't accepting say-hi requests right now",
  "table.askIgMessage": "Mind sharing your IG?",
  "table.softGreetMessage": "A gentle hello — nothing is shared until they accept",
  "table.requestSentToast": "Sent your {subject} request to table {table} · it's their call",
  "table.consentShareNote":
    "Extra photos and IG are shared between you only after a say-hi request is accepted",
  "table.giftGuardPrefix": "Send a drink: {reason}",
  "table.contactGuardPrefix": "Say hi: {reason}",
  "table.askIg": "Ask for IG",
  "table.softGreet": "Send a gentle hello",
  "table.blockedToast": "Table blocked",
  "table.reportToast": "Report sent · staff moderation will see it",
  "table.statusOpenOrders": "Taking orders",
  "table.statusInUse": "Occupied",

  // Room / floor plan
  "room.loadingFloor": "Loading the floor plan…",
  "room.floorTonight": "Tonight's floor plan",
  "room.yourTablePrefix": "Your table is",
  "room.tapOtherToSend": "tap another table to send a drink",
  "room.tapToSend": "Tap a table to send a drink",
  "room.sheetDesc": "Send a drink without revealing yourself · accepting is up to them",
  "room.sendDrink": "Send a drink",
  "room.reportToast": "Reported — the team will take a look",

  // Send-drink (gift) flow
  "send.noTarget": "Couldn't find the target table",
  "send.sealedToast": "Sealed and sent to table {table}",
  "send.fromYou": "Table {table} (you)",
  "send.sentNote": "Sent · they accept first, then the venue serves it",
  "send.mockStatus": "Mock status: {status}",
  "send.igAttachedShort": "IG request attached",
  "send.kicker": "A light little gift",
  "send.selectedFallback": "your pick",
  "send.currentMode": "Your mode right now: {mode}",
  "send.modeGateNote": "Private mode or contact-off blocks any request that skips consent",
  "send.chooseItem": "Pick a drink",
  "send.loadingGifts": "Loading gifts from the API...",
  "send.loadError": "Couldn't load from the API — no mock fallback in backend mode",
  "send.attachMessage": "Add a message",
  "send.attachIg": "Attach an IG request (they can accept or decline)",
  "send.contactOffWarning": "You've turned contact off — turn it on before attaching IG",
  "send.sealAndSend": "Seal it and send",
  "send.confirmTitle": "Send it?",
  "send.confirmDesc": "They accept first — then the venue serves {item} to table {table}",
  "send.confirmSeal": "Seal & send",

  // Games (coming soon)
  "games.kicker": "While you wait",
  "games.title": "Play something light",
  "games.soon": "Coming soon",
  "games.soonBody": "Games and between-round activities are in the works. No bets, no cash prizes",

  // Screen (pay-to-post) flow
  "screen.guardTitle": "On the big screen",
  "screen.titleBig": "On the big screen 📺",
  "screen.checkInFirst": "Check in at a table first to use this",
  "screen.kicker": "Post on the big screen",
  "screen.pickHint": "Pick a post type · pay the staff to get it on screen",
  "screen.type.messageDesc": "Put a message on screen",
  "screen.type.warpDesc": "Drop your IG / show a photo",
  "screen.type.songDesc": "Request a song from the DJ",
  "screen.type.tipDesc": "Tip the artists / staff",
  "screen.next": "Next →",
  "screen.messageLabel": "Message (max 120 characters) *",
  "screen.messagePlaceholder": "Type the message you want on screen...",
  "screen.igLabel": "IG handle (no @ needed)",
  "screen.photoLabel": "Photo (optional)",
  "screen.uploading": "Uploading...",
  "screen.uploaded": "Uploaded ✓",
  "screen.choosePhoto": "Choose a photo (JPEG/PNG)",
  "screen.captionLabel": "Caption (optional)",
  "screen.captionPlaceholder": "A short line...",
  "screen.songLabel": "Song you're requesting *",
  "screen.songPlaceholder": "Song title — artist",
  "screen.tipTargetLabel": "Tip recipient *",
  "screen.loadingShort": "Loading...",
  "screen.noTipTargets": "No tip recipients right now",
  "screen.tipAmountLabel": "Tip amount (baht) · minimum {min}",
  "screen.uploadFailedWith": "Upload failed: {error}",
  "screen.uploadFailed": "Upload failed",
  "screen.postFailed": "Post failed",
  "screen.mockNote": "Mock mode — posts aren't sent to the real backend",
  "screen.submitting": "Sending...",
  "screen.submit": "Post it 📺",
  "screen.successTitle": "Posted!",
  "screen.successBody": "Posted! Pay the staff to get your message up on screen",
  "screen.typeLine": "Type: {type}",
  "screen.postAgain": "Post again",
  "screen.myPosts": "My posts",
  "screen.pdpaNote":
    "This will show on the venue's big screen for everyone to see · the name shown is your check-in name",
  "screen.showAvatar": "Show my LINE avatar on screen",
  "screen.avatarLockedNote": "Always on for IG drops",
  "screen.showTable": "Show my table number on screen",

  // Screen domain validation + status labels
  "screen.validation.messageRequired": "Please enter a message",
  "screen.validation.messageTooLong": "Message is over 120 characters",
  "screen.validation.warpNeedsIgOrPhoto": "Add an IG handle or upload a photo — at least one",
  "screen.validation.igTooLong": "IG handle is over 60 characters",
  "screen.validation.songRequired": "Please enter a song title",
  "screen.validation.songTooLong": "Song title is over 120 characters",
  "screen.validation.tipTargetRequired": "Please pick a tip recipient",
  "screen.validation.tipAmountRequired": "Please enter a tip amount",
  "screen.status.submitted": "Awaiting payment with staff",
  "screen.status.paid": "Awaiting approval",
  "screen.status.approved": "Going on screen soon ✨",
  "screen.status.displayed": "On screen 🎉",
  "screen.status.rejected": "Not approved",
  "screen.status.removed": "Taken down",
  "screen.status.expired": "Expired",
  "screen.postType.message": "Send a message",
  "screen.postType.warp": "IG drop",
  "screen.postType.song_request": "Request a song",
  "screen.postType.tip": "Give a tip",

  // Session-level toasts + consent guards
  "session.checkInFailed": "Check-in failed",
  "session.orderRetrying": "Network hiccup · retrying your order automatically",
  "session.giftRetrying": "Network hiccup · retrying your drink automatically",
  "session.modeChangeFailed": "Couldn't change mode",
  "session.settingsSaveFailed": "Couldn't save your settings",
  "session.giftSendFailed": "Couldn't send the drink",
  "session.contactSendFailed": "Couldn't send the say-hi request",
  "session.blockFailed": "Couldn't block",
  "session.reportFailed": "Couldn't send the report",
  "session.giftRespondFailed": "Couldn't respond to the gift",
  "session.contactRespondFailed": "Couldn't respond to the say-hi request",
  "session.leaveFailed": "Couldn't leave the venue",
  "guard.privateMode":
    "You're still in private mode · open up tonight's mode before sending requests",
  "guard.blockedTable": "You've blocked table {table}",
  "guard.contactOff": "You've turned say-hi requests off, so contact requests can't be sent",
  "guard.contactOffIg": "Turn say-hi requests on before attaching IG",
  "guard.duplicateRequest":
    "You already have a pending request to table {table} · wait for their reply",
  "guard.orderActive":
    "The venue is still handling an order · have staff close it before you leave",
};
